import {
  Box,
  Button,
  Checkbox,
  Divider,
  Heading,
  Inline,
  Stack,
  Text,
  useForegroundVariantColor
} from "@seneca/senekit";
import {
  ContactPreferenceOptions,
  ContactPreferences,
  UnsubscribeWithEncryptedTopicResponseBody
} from "@seneca/notifications-service-types";

import { PreferenceOptions } from "../../../../notifications/types";
import { useGetPreferencesOptions } from "../../hooks/useGetPreferencesOptions";
import { useContactPreferencesState } from "../hooks/useContactPreferencesState";
import { getIsTopicChecked } from "../utils/getIsTopicChecked";

type Props = {
  initialContactPreferences:
    | UnsubscribeWithEncryptedTopicResponseBody
    | undefined;
  hasLoadedInitialContactPreferences: boolean;
  updatePreferences: (preferences: Partial<ContactPreferences>) => void;
};

export function EmailSettings({
  initialContactPreferences,
  hasLoadedInitialContactPreferences,
  updatePreferences
}: Props) {
  const foregroundVariantColor = useForegroundVariantColor("1");
  const {
    preferences,
    updatePreferences: updatePreferencesState,
    getIsButtonDisabled,
    getPreferencesDiffs
  } = useContactPreferencesState(
    initialContactPreferences,
    hasLoadedInitialContactPreferences
  );

  const preferenceOptions = useGetPreferencesOptions(
    preferences?.unsubscribedTopic as keyof ContactPreferenceOptions
  );

  if (!preferenceOptions.length) {
    return null;
  }

  return (
    <Box
      background={foregroundVariantColor}
      padding="xxlarge"
      borderRadius="large"
    >
      <Stack space="xxlarge">
        <Heading level="4" weight="normal">
          Email notification settings
        </Heading>
        <Divider />
        {preferenceOptions.map((option, index) => {
          const field = option.name as keyof PreferenceOptions;
          const isTopicChecked = getIsTopicChecked(preferences, field);

          return (
            <>
              <Inline
                justifyContent="space-between"
                alignItems="center"
                flexWrap="nowrap"
                space="xlarge"
              >
                <Text
                  weight={
                    index === preferenceOptions.length - 1 ? "bold" : undefined
                  }
                >
                  {option.description}
                </Text>
                <Checkbox
                  checked={isTopicChecked}
                  name={`email-settings-${field}`}
                  onChange={() => updatePreferencesState(field)}
                  disabled={field !== "subscribed" && !preferences?.subscribed}
                />
              </Inline>
              {index === preferenceOptions.length - 2 && <Divider />}
            </>
          );
        })}
        <Button
          disabled={getIsButtonDisabled()}
          onClick={() => {
            updatePreferences(getPreferencesDiffs());
          }}
        >
          Save changes
        </Button>
      </Stack>
    </Box>
  );
}
